import React, { useState } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 标题区块样式
const HeaderSection = styled('section', {
  textAlign: 'center',
  marginBottom: '2rem'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '$textPrimary'
});

// 副标题样式
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 筛选器容器样式
const FiltersContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '1.5rem',
  padding: '1.5rem',
  background: '$backgroundSecondary',
  borderRadius: '12px',
  marginBottom: '2rem',
  justifyContent: 'center',
  
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'center'
  }
});

// 按钮筛选器样式已删除 - 当前未使用

// 下拉筛选器组样式
const FilterGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  minWidth: '180px'
});

// 筛选器标签样式
const FilterLabel = styled('label', {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 筛选器选择框样式
const FilterSelect = styled('select', {
  padding: '0.75rem',
  borderRadius: '8px',
  border: '1px solid $borderColor',
  background: '$backgroundPrimary',
  color: '$textPrimary',
  fontSize: '1rem',
  
  '&:focus': {
    outline: 'none',
    borderColor: '$accentPrimary'
  }
});

// 合约网格容器样式
const ContractsGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: '2rem',
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr'
  }
});

// 合约卡片样式
const ContractCard = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  background: '$backgroundSecondary',
  borderRadius: '12px',
  overflow: 'hidden',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  position: 'relative',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
  },
  
  variants: {
    popular: {
      true: {
        border: '2px solid $accentPrimary'
      }
    }
  }
});

// 人气标签样式
const PopularTag = styled('div', {
  position: 'absolute',
  top: '12px',
  right: '12px',
  background: '$accentPrimary',
  color: 'white',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  padding: '0.25rem 0.75rem',
  borderRadius: '20px',
  zIndex: 1
});

// 合约头部样式
const ContractHeader = styled('div', {
  position: 'relative',
  overflow: 'hidden',
  padding: '1.5rem',
  background: '$backgroundTertiary',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem'
});

// 算法图标样式
const AlgorithmIcon = styled('div', {
  width: '48px',
  height: '48px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.5rem',
  color: 'white',
  background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)'
});

// 合约标题样式
const ContractTitle = styled('h3', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 合约算法样式
const ContractAlgorithm = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary'
});

// 合约价格样式
const ContractPrice = styled('div', {
  fontSize: '1.75rem',
  fontWeight: 'bold',
  color: '$textPrimary',
  marginTop: '0.5rem',
  display: 'flex',
  alignItems: 'baseline'
});

// 合约价格单位样式
const PriceUnit = styled('span', {
  fontSize: '1rem',
  fontWeight: 'normal',
  color: '$textSecondary',
  marginLeft: '0.25rem'
});

// 合约详情样式
const ContractDetails = styled('div', {
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.25rem'
});

// 详情项样式
const DetailItem = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});

// 详情标签样式
const DetailLabel = styled('span', {
  fontSize: '1rem',
  color: '$textSecondary'
});

// 详情值样式
const DetailValue = styled('span', {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 预期回报容器样式
const ReturnContainer = styled('div', {
  padding: '1rem',
  background: '$backgroundTertiary',
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

// 预期回报标签样式
const ReturnLabel = styled('span', {
  fontSize: '1rem',
  color: '$textSecondary'
});

// 预期回报值样式
const ReturnValue = styled('span', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  color: '$accentPrimary'
});

// 合约按钮容器样式
const ContractButtonContainer = styled('div', {
  padding: '0 1.5rem 1.5rem',
  display: 'flex',
  gap: '1rem'
});

// 合约按钮样式
const ContractButton = styled('button', {
  flex: 1,
  padding: '0.75rem 1.5rem',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  variants: {
    variant: {
      primary: {
        background: '$accentPrimary',
        color: 'white',
        border: 'none',
        '&:hover': {
          opacity: 0.9
        }
      },
      secondary: {
        background: 'transparent',
        color: '$textPrimary',
        border: '1px solid $borderColor',
        '&:hover': {
          background: '$backgroundTertiary'
        }
      }
    }
  },
  
  defaultVariants: {
    variant: 'primary'
  }
});

// 分页容器样式
const PaginationContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  gap: '0.5rem',
  marginTop: '2rem'
});

// 分页按钮样式
const PaginationButton = styled('button', {
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  border: '1px solid $borderColor',
  background: '$backgroundPrimary',
  color: '$textPrimary',
  fontSize: '1rem',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    background: '$backgroundTertiary'
  },
  
  variants: {
    active: {
      true: {
        background: '$accentPrimary',
        color: 'white',
        border: 'none',
        '&:hover': {
          opacity: 0.9
        }
      }
    },
    disabled: {
      true: {
        opacity: 0.5,
        cursor: 'not-allowed',
        '&:hover': {
          background: '$backgroundPrimary'
        }
      }
    }
  }
});

// 计算器链接样式
const CalculatorLink = styled('div', {
  textAlign: 'center',
  marginTop: '3rem'
});

// 链接文本样式
const LinkText = styled('p', {
  fontSize: '1.1rem',
  color: '$textSecondary',
  marginBottom: '1rem'
});

// 链接按钮样式
const LinkButton = styled('button', {
  padding: '0.75rem 1.5rem',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 'bold',
  background: 'transparent',
  color: '$accentPrimary',
  border: '1px solid $accentPrimary',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    background: 'rgba(6, 214, 160, 0.1)'
  }
});

// 无结果容器样式
const NoResultsContainer = styled('div', {
  padding: '3rem',
  textAlign: 'center',
  background: '$backgroundSecondary',
  borderRadius: '12px'
});

// 无结果文本样式
const NoResultsText = styled('p', {
  fontSize: '1.25rem',
  color: '$textSecondary',
  marginBottom: '1.5rem'
});

// 重置按钮样式
const ResetButton = styled('button', {
  padding: '0.75rem 1.5rem',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 'bold',
  background: '$accentPrimary',
  color: 'white',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    opacity: 0.9
  }
});

/**
 * 云挖矿合约页面组件
 * 展示可用的云挖矿合约
 */
const CloudMiningContracts: React.FC = () => {
  const { t } = useTranslation();
  
  // 筛选条件状态
  const [algorithm, setAlgorithm] = useState('all');
  const [duration, setDuration] = useState('all');
  const [sortBy, setSortBy] = useState('price-asc');
  const [currentPage, setCurrentPage] = useState(1);
  
  // 每页显示数量
  const contractsPerPage = 6;
  
  // 合约数据
  const contractsData = [
    {
      id: 1,
      title: 'BTC Basic',
      algorithm: 'SHA-256',
      algorithmIcon: '₿',
      price: 300,
      duration: 180, // 天
      hashrate: '5.5 TH/s',
      efficiency: '38 W/TH',
      maintenance: '$0.18/TH/天',
      expectedReturn: '12-15%',
      popular: false,
      coin: 'BTC'
    },
    {
      id: 2,
      title: 'BTC Standard',
      algorithm: 'SHA-256',
      algorithmIcon: '₿',
      price: 700,
      duration: 360, // 天
      hashrate: '13 TH/s',
      efficiency: '35 W/TH',
      maintenance: '$0.16/TH/天',
      expectedReturn: '15-20%',
      popular: true,
      coin: 'BTC'
    },
    {
      id: 3,
      title: 'BTC Pro',
      algorithm: 'SHA-256',
      algorithmIcon: '₿',
      price: 1800,
      duration: 720, // 天
      hashrate: '35 TH/s',
      efficiency: '32 W/TH',
      maintenance: '$0.15/TH/天',
      expectedReturn: '18-25%',
      popular: false,
      coin: 'BTC'
    },
    {
      id: 4,
      title: 'ETH Basic',
      algorithm: 'Ethash',
      algorithmIcon: 'Ξ',
      price: 250,
      duration: 180, // 天
      hashrate: '100 MH/s',
      efficiency: '2.8 W/MH',
      maintenance: '$0.04/MH/天',
      expectedReturn: '10-13%',
      popular: false,
      coin: 'ETH'
    },
    {
      id: 5,
      title: 'ETH Standard',
      algorithm: 'Ethash',
      algorithmIcon: 'Ξ',
      price: 600,
      duration: 360, // 天
      hashrate: '250 MH/s',
      efficiency: '2.5 W/MH',
      maintenance: '$0.035/MH/天',
      expectedReturn: '14-18%',
      popular: false,
      coin: 'ETH'
    },
    {
      id: 6,
      title: 'ETH Pro',
      algorithm: 'Ethash',
      algorithmIcon: 'Ξ',
      price: 1500,
      duration: 720, // 天
      hashrate: '650 MH/s',
      efficiency: '2.2 W/MH',
      maintenance: '$0.03/MH/天',
      expectedReturn: '16-22%',
      popular: true,
      coin: 'ETH'
    },
    {
      id: 7,
      title: 'LTC Basic',
      algorithm: 'Scrypt',
      algorithmIcon: 'Ł',
      price: 200,
      duration: 180, // 天
      hashrate: '10 GH/s',
      efficiency: '1 W/GH',
      maintenance: '$0.05/GH/天',
      expectedReturn: '9-12%',
      popular: false,
      coin: 'LTC'
    },
    {
      id: 8,
      title: 'LTC Standard',
      algorithm: 'Scrypt',
      algorithmIcon: 'Ł',
      price: 500,
      duration: 360, // 天
      hashrate: '25 GH/s',
      efficiency: '0.9 W/GH',
      maintenance: '$0.045/GH/天',
      expectedReturn: '12-16%',
      popular: false,
      coin: 'LTC'
    },
    {
      id: 9,
      title: 'DOGE Basic',
      algorithm: 'Scrypt',
      algorithmIcon: 'Ð',
      price: 180,
      duration: 180, // 天
      hashrate: '9 GH/s',
      efficiency: '1 W/GH',
      maintenance: '$0.05/GH/天',
      expectedReturn: '8-12%',
      popular: false,
      coin: 'DOGE'
    },
    {
      id: 10,
      title: 'DOGE Standard',
      algorithm: 'Scrypt',
      algorithmIcon: 'Ð',
      price: 450,
      duration: 360, // 天
      hashrate: '23 GH/s',
      efficiency: '0.9 W/GH',
      maintenance: '$0.045/GH/天',
      expectedReturn: '11-15%',
      popular: false,
      coin: 'DOGE'
    }
  ];
  
  // 应用筛选和排序
  const filteredContracts = contractsData.filter(contract => {
    // 筛选算法
    if (algorithm !== 'all' && contract.algorithm.toLowerCase() !== algorithm) {
      return false;
    }
    
    // 筛选周期
    if (duration === '180' && contract.duration !== 180) return false;
    if (duration === '360' && contract.duration !== 360) return false;
    if (duration === '720' && contract.duration !== 720) return false;
    
    return true;
  }).sort((a, b) => {
    // 排序
    switch (sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'roi-asc':
        // 转换ROI为数字（取平均值）
        const roiA = parseFloat(a.expectedReturn.split('-')[0]) + parseFloat(a.expectedReturn.split('-')[1]);
        const roiB = parseFloat(b.expectedReturn.split('-')[0]) + parseFloat(b.expectedReturn.split('-')[1]);
        return roiA - roiB;
      case 'roi-desc':
        // 转换ROI为数字（取平均值）
        const roiADesc = parseFloat(a.expectedReturn.split('-')[0]) + parseFloat(a.expectedReturn.split('-')[1]);
        const roiBDesc = parseFloat(b.expectedReturn.split('-')[0]) + parseFloat(b.expectedReturn.split('-')[1]);
        return roiBDesc - roiADesc;
      case 'duration-asc':
        return a.duration - b.duration;
      case 'duration-desc':
        return b.duration - a.duration;
      default:
        return 0;
    }
  });
  
  // 分页
  const totalPages = Math.ceil(filteredContracts.length / contractsPerPage);
  const indexOfLastContract = currentPage * contractsPerPage;
  const indexOfFirstContract = indexOfLastContract - contractsPerPage;
  const currentContracts = filteredContracts.slice(indexOfFirstContract, indexOfLastContract);
  
  // 分页处理
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  
  // 下一页
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // 上一页
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // 重置筛选
  const resetFilters = () => {
    setAlgorithm('all');
    setDuration('all');
    setSortBy('price-asc');
    setCurrentPage(1);
  };
  
  return (
    <PageContainer>
      <HeaderSection>
        <Title>{t('cloudMining.contracts.title', '云挖矿合约列表')}</Title>
        <Subtitle>
          {t('cloudMining.contracts.subtitle', '浏览我们的云挖矿合约，选择最适合您投资需求的挖矿计划')}
        </Subtitle>
      </HeaderSection>
      
      {/* 筛选器区域 */}
      <FiltersContainer>
        <FilterGroup>
          <FilterLabel htmlFor="algorithm-filter">{t('cloudMining.contracts.filters.algorithm', '挖矿算法')}</FilterLabel>
          <FilterSelect 
            id="algorithm-filter"
            value={algorithm} 
            onChange={(e) => setAlgorithm(e.target.value)}
            title={t('cloudMining.contracts.filters.algorithm', '挖矿算法')}
            aria-label={t('cloudMining.contracts.filters.algorithm', '挖矿算法')}
            name="algorithm-filter"
          >
            <option value="all">{t('cloudMining.contracts.filters.all', '全部')}</option>
            <option value="sha-256">SHA-256 (BTC)</option>
            <option value="ethash">Ethash (ETH)</option>
            <option value="scrypt">Scrypt (LTC/DOGE)</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel htmlFor="duration-filter">{t('cloudMining.contracts.filters.duration', '合约周期')}</FilterLabel>
          <FilterSelect 
            id="duration-filter"
            value={duration} 
            onChange={(e) => setDuration(e.target.value)}
            title={t('cloudMining.contracts.filters.duration', '合约周期')}
            aria-label={t('cloudMining.contracts.filters.duration', '合约周期')}
            name="duration-filter"
          >
            <option value="all">{t('cloudMining.contracts.filters.all', '全部')}</option>
            <option value="180">180 {t('cloudMining.contracts.filters.days', '天')}</option>
            <option value="360">360 {t('cloudMining.contracts.filters.days', '天')}</option>
            <option value="720">720 {t('cloudMining.contracts.filters.days', '天')}</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel htmlFor="sort-filter">{t('cloudMining.contracts.filters.sortBy', '排序方式')}</FilterLabel>
          <FilterSelect 
            id="sort-filter"
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            title={t('cloudMining.contracts.filters.sortBy', '排序方式')}
            aria-label={t('cloudMining.contracts.filters.sortBy', '排序方式')}
            name="sort-filter"
          >
            <option value="price-asc">{t('cloudMining.contracts.filters.priceLowToHigh', '价格 (从低到高)')}</option>
            <option value="price-desc">{t('cloudMining.contracts.filters.priceHighToLow', '价格 (从高到低)')}</option>
            <option value="roi-asc">{t('cloudMining.contracts.filters.roiLowToHigh', '回报率 (从低到高)')}</option>
            <option value="roi-desc">{t('cloudMining.contracts.filters.roiHighToLow', '回报率 (从高到低)')}</option>
            <option value="duration-asc">{t('cloudMining.contracts.filters.durationShortToLong', '周期 (从短到长)')}</option>
            <option value="duration-desc">{t('cloudMining.contracts.filters.durationLongToShort', '周期 (从长到短)')}</option>
          </FilterSelect>
        </FilterGroup>
      </FiltersContainer>
      
      {/* 合约列表 */}
      {currentContracts.length > 0 ? (
        <ContractsGrid>
          {currentContracts.map(contract => (
            <ContractCard key={contract.id} popular={contract.popular}>
              {contract.popular && (
                <PopularTag>{t('cloudMining.contracts.popular', '热门选择')}</PopularTag>
              )}
              <ContractHeader>
                <AlgorithmIcon>{contract.algorithmIcon}</AlgorithmIcon>
                <ContractTitle>{contract.title}</ContractTitle>
                <ContractAlgorithm>{contract.algorithm} - {contract.coin}</ContractAlgorithm>
                <ContractPrice>
                  ${contract.price}
                  <PriceUnit> USD</PriceUnit>
                </ContractPrice>
              </ContractHeader>
              <ContractDetails>
                <DetailItem>
                  <DetailLabel>{t('cloudMining.contracts.hashrate', '算力')}</DetailLabel>
                  <DetailValue>{contract.hashrate}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>{t('cloudMining.contracts.duration', '合约周期')}</DetailLabel>
                  <DetailValue>{contract.duration} {t('cloudMining.contracts.days', '天')}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>{t('cloudMining.contracts.efficiency', '能效比')}</DetailLabel>
                  <DetailValue>{contract.efficiency}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>{t('cloudMining.contracts.maintenance', '维护费')}</DetailLabel>
                  <DetailValue>{contract.maintenance}</DetailValue>
                </DetailItem>
                <ReturnContainer>
                  <ReturnLabel>{t('cloudMining.contracts.expectedReturn', '预期年化收益')}</ReturnLabel>
                  <ReturnValue>{contract.expectedReturn}</ReturnValue>
                </ReturnContainer>
              </ContractDetails>
              <ContractButtonContainer>
                <ContractButton variant="primary" as={Link} to="https://h5-app.BITENGINE.com" target="_blank">
                  {t('cloudMining.contracts.buyNow', '立即购买')}
                </ContractButton>
                <ContractButton variant="secondary" as={Link} to="/products/cloud-mining/calculator">
                  {t('cloudMining.contracts.calculate', '计算收益')}
                </ContractButton>
              </ContractButtonContainer>
            </ContractCard>
          ))}
        </ContractsGrid>
      ) : (
        <NoResultsContainer>
          <NoResultsText>{t('cloudMining.contracts.noResults', '没有找到符合条件的合约')}</NoResultsText>
          <ResetButton onClick={resetFilters}>
            {t('cloudMining.contracts.resetFilters', '重置筛选条件')}
          </ResetButton>
        </NoResultsContainer>
      )}
      
      {/* 分页 */}
      {filteredContracts.length > contractsPerPage && (
        <PaginationContainer>
          <PaginationButton 
            onClick={prevPage} 
            disabled={currentPage === 1}
            aria-label={t('cloudMining.contracts.previousPage', '上一页')}
          >
            &lt;
          </PaginationButton>
          
          {Array.from({ length: totalPages }, (_, index) => index + 1).map(number => (
            <PaginationButton
              key={number}
              active={currentPage === number}
              onClick={() => handlePageChange(number)}
              aria-label={t('cloudMining.contracts.gotoPage', '转到第') + number + t('cloudMining.contracts.page', '页')}
            >
              {number}
            </PaginationButton>
          ))}
          
          <PaginationButton 
            onClick={nextPage} 
            disabled={currentPage === totalPages}
            aria-label={t('cloudMining.contracts.nextPage', '下一页')}
          >
            &gt;
          </PaginationButton>
        </PaginationContainer>
      )}
      
      {/* 计算器链接 */}
      <CalculatorLink>
        <LinkText>{t('cloudMining.contracts.calculatorPromo', '想要更精确地了解您的投资回报？使用我们的挖矿收益计算器')}</LinkText>
        <Link to="/products/cloud-mining/calculator">
          <LinkButton>{t('cloudMining.contracts.openCalculator', '打开收益计算器')}</LinkButton>
        </Link>
      </CalculatorLink>
    </PageContainer>
  );
};

export default CloudMiningContracts; 